import * as React from 'react';
import {
  Button,
  Stack,
  Box,
  Typography,
  Collapse,
  styled,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import {UserFilled, WarningAltFilled, ChevronUp, ChevronDown} from '@carbon/icons-react';
import Block from 'assets/images/block_black_18dp.svg';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import ResourceModelSelection from './ResourceModelSelection';
//Local Imports
import {IPhase, IProcess, phaseColorsConstants, DefaultIPhase, LogInType} from 'app/types/Shared';
import {useSelector, useDispatch} from 'react-redux';
import {
  selectRoleFilters,
  selectSelectedPhase,
  setSelectedPhase,
  selectParentOpen,
  setSelectedParentOpen,
  selectProcessModelSelections,
  setProcessModelSelections,
  selectOnboarding,
} from 'app/store/appState/appStateSlice';
import {
  selectAllProcesses,
  selectAllPhases,
  selectProcessesWithMultipleModels,
} from 'app/store/operatingModel/operatingModelSlice';
import {selectUserType} from 'app/store/users/userSlice';

const ProcessBox = styled(Box)({
  width: '100%',
  border: 'solid 0.2px lightgray',
  minHeight: '30px',
  maxHeight: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

type navProps = {
  selectedGroup: string;
  processClickCallback: (process: IProcess) => void;
};

const disablePhases = ['Portfolio Enabling'];

const ProcessNavigation: React.FC<navProps> = ({selectedGroup, processClickCallback}: navProps) => {
  const theme = useTheme();
  const phases = useSelector(selectAllPhases);
  const allProcesses = useSelector(selectAllProcesses);
  const selectedRoles = useSelector(selectRoleFilters);
  const currPhase = useSelector(selectSelectedPhase);
  const parentOpen = useSelector(selectParentOpen);
  const processWMultipleModels = useSelector(selectProcessesWithMultipleModels);
  const processModelSelections = useSelector(selectProcessModelSelections);
  const onboarding = useSelector(selectOnboarding);
  const userType = useSelector(selectUserType);

  const [openModelSelector, setOpenModelSelector] = React.useState(false);
  const [processSelected, setProcessSelected] = React.useState('');
  const [isPressed, setIsPressed] = React.useState(false);

  const dispatch = useDispatch();
  const {trackEvent} = useMatomo();

  const getPhaseColor = (phase?: string) => {
    if (phase) {
      const color = phaseColorsConstants.find((x) => x.name === phase)?.color;
      if (color) {
        return color;
      }
    }
    return 'inherit';
  };
  const [highlightColor, setHighlightColor] = React.useState<string>(getPhaseColor(currPhase?.name)); //current highlight color

  const groupProcesses = allProcesses?.filter((x) => x.category_group === selectedGroup);
  const processByGroup = groupProcesses?.reduce((acc, curr) => {
    if (curr.parent_process === null) {
      acc.push({parent: curr, children: []});
    } else {
      const index = acc.findIndex((x) => x.parent.id === curr.parent_process_id);
      if (index != -1) {
        if (processWMultipleModels?.some((process) => process.process_id === curr.process_id)) {
          // Only add if this process name hasn't been added yet to avoid duplicates
          const alreadyAdded = acc[index].children.some((child) => child.name === curr.name);
          if (!alreadyAdded && ['Hybrid', 'Insourced', 'Outsourced'].includes(curr.operating_models_list ?? '')) {
            acc[index].children.push(curr);
          }
        } else {
          acc[index].children.push(curr);
        }
      }
    }
    return acc;
  }, [] as Array<{parent: IProcess; children: Array<IProcess>}>);

  //update the current phase and color on phase click
  const handlePhaseClick = (phase: IPhase) => {
    if (currPhase) {
      if (currPhase.id === phase.id) {
        dispatch(setSelectedPhase(DefaultIPhase));
        setHighlightColor('#eeeeee');
      } else {
        colorSet(phase.name);
        dispatch(setSelectedPhase(phase));
      }
    } else {
      colorSet(phase.name);
      dispatch(setSelectedPhase(phase));
    }
    trackEvent({category: 'Navigation', action: 'Process Phase Selected', name: phase.name});
  };

  //update the current process
  const handleProcessClick = (process?: IProcess) => {
    if (process) {
      if (process.diagram_link) {
        if (processWMultipleModels?.map((p) => p.process_id)?.includes(process?.process_id)) {
          const findModel = processModelSelections?.find((x) => x.process_name === process.name);
          if (findModel) {
            const newProcess = allProcesses?.find(
              (x) => x.name === process.name && x.operating_models_list === findModel.model,
            );
            if (newProcess) {
              processClickCallback(newProcess);
            } else {
              setProcessSelected(process.name);
              setOpenModelSelector(true);
            }
          } else {
            setProcessSelected(process.name);
            setOpenModelSelector(true);
          }
        } else {
          processClickCallback(process);
        }
      }
    }
  };

  //set highlight color based on phase clicked
  const colorSet = (phase: string) => {
    const findColor = phaseColorsConstants.find((phaseItem) => phaseItem.name == phase);
    if (findColor) {
      setHighlightColor(findColor.color);
    } else setHighlightColor('white');
  };

  const childrenHaveRoles = (children: Array<IProcess>) => {
    return children.some((child) =>
      child.roles.some((r) =>
        selectedRoles?.includes(r.role.role_category.label ? r.role.role_category.label : r.role.role_category.name),
      ),
    );
  };

  const childrenHaveLinks = (children: Array<IProcess>) => {
    return children.some((child) => child.diagram_link !== null);
  };

  const getToolTip = (process?: IProcess) => {
    if (process) {
      const charMax = (process.ui_mark_duration / 4) * 18;

      if (process.name.length > charMax) {
        return true;
      }
    }
    return false;
  };

  const ProcessToolTip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'white',
      maxWidth: 'none',
      boxShadow: `0px 0px 10px 0px ${theme.palette.grey[700]}`,
    },
  });

  const onConfirmResourceSelection = (model: string) => {
    const process_id = processWMultipleModels?.find((p) => p.model === model && p.name === processSelected)?.process_id;
    if (process_id) {
      const process = allProcesses?.find((process) => process.process_id === process_id);
      if (process) {
        if (process.diagram_link) {
          if (!processModelSelections?.includes({process_name: process.name, model: model})) {
            dispatch(setProcessModelSelections({process_name: process.name, model: model}));
          }
          setOpenModelSelector(false);
          setProcessSelected('');
          processClickCallback(process);
        }
      }
    }
  };

  //Render 2 Grids. One for Phases, One for Processes, both set on a 28.8 scale.
  //First 3 cells on each grid are for process groups sidebar. Last 0.5 on each grid is for buffer from right side of container
  //Phase and Processes content is on the operating model duration unit scale (for clean numbers)
  return (
    <>
      <Box sx={{minWidth: 700, height: '100%', maxWidth: 1200}}>
        <Box width='100%' height='100%' position='relative'>
          <ResourceModelSelection
            open={openModelSelector}
            processTitle={processSelected}
            onClose={() => {
              setProcessSelected('');
              setOpenModelSelector(false);
            }}
            onConfirm={onConfirmResourceSelection}
          />
          {userType === LogInType.Internal && onboarding?.roleGuide && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(246, 248, 249, 0.6)', // 15% opacity
                zIndex: 2,
              }}
            />
          )}
          <Grid
            container
            alignContent='flex-start'
            direction='row'
            justifyContent='flex-start'
            columns={32.5}
            columnSpacing='5px'
            paddingBottom='5px'
          >
            {phases &&
              phases.map((item, index) => (
                <React.Fragment key={`Phases-${index}`}>
                  {item.mark_width && (
                    <Grid xs={item.mark_width} md={item.mark_width} key={`${item.name}.phases.${index}`}>
                      <Button
                        id={'Phase-' + index}
                        disabled={disablePhases.includes(selectedGroup)}
                        sx={{
                          borderRadius: 0,
                          width: '100%',
                          variant: 'contained',
                          minHeight: '100%',
                          justifyContent: 'center',
                          height: 40,
                          pt: 0.5,
                          pb: 0.5,
                          px: 4,
                          lineHeight: 1,
                          gap: '10px',
                          fontSize: 12,
                          borderBottom: `3px solid ${getPhaseColor(item.name)}`,
                          background: isPressed ? getPhaseColor(item.name) + '26' : theme.palette.grey[400],
                          color: theme.palette.grey[700],
                          '&:hover': {
                            background: getPhaseColor(item.name) + '0D',
                            color: theme.palette.grey[700],
                          },
                          '&.Mui-disabled': {
                            backgroundColor: theme.palette.grey[400],
                            color: theme.palette.grey[700],
                            borderBottom: `3px solid ${theme.palette.grey[700]}`,
                            opacity: 0.35,
                          },
                          zIndex: 1,
                        }}
                        disableRipple
                        onClick={() => {
                          console.log('handlePhaseClick');
                          handlePhaseClick(item);
                        }}
                        onMouseDown={() => item.id === currPhase?.id && setIsPressed(true)}
                        onMouseUp={() => item.id === currPhase?.id && setIsPressed(false)}
                        onMouseLeave={() => item.id === currPhase?.id && setIsPressed(false)}
                      >
                        {item.name}
                      </Button>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
          </Grid>
          {processByGroup
            ?.filter((i) => i.children.length > 0)
            .map((item, index) => (
              <Grid container direction='row' key={`${item.parent.label}.group.${index}`} columns={32.5}>
                <Grid xs={32} mb={0.5}>
                  <Grid container direction='row' key={`${item.parent.name}`} columns={32} columnSpacing='5px' mb='1px'>
                    <>
                      <Grid
                        xsOffset={item.parent.ui_mark_offset}
                        xs={item.parent.ui_mark_duration}
                        key={`${item.parent.name}.process.${item.parent.id}`}
                      >
                        {item.children.length === 1 &&
                        item.children.at(0) &&
                        item.children.at(0)?.name.replaceAll(' ', '') === item.parent.name.replaceAll(' ', '') ? (
                          <>
                            <ProcessBox
                              sx={{
                                bgcolor: item.children.at(0)?.diagram_link ? '#6A3B3B' : '#E1E3E4',
                                border: item.children.at(0)?.diagram_link ? 'inherit' : 'none',
                                cursor: `url(${Block}), auto`,
                                '&:hover': item.children.at(0)?.diagram_link && {
                                  background: theme.palette.primary.main,
                                  cursor: 'pointer',
                                },
                              }}
                              onClick={() => handleProcessClick(item.children.at(0))}
                            >
                              <ProcessToolTip
                                title={
                                  <Typography variant='h5' color={theme.palette.grey[700]}>
                                    {item.children.at(0)?.name}
                                  </Typography>
                                }
                                sx={{display: getToolTip(item.parent) ? 'inherit' : 'none'}}
                                placement='bottom-start'
                              >
                                <Stack
                                  direction='row'
                                  spacing='10px'
                                  alignItems='center'
                                  justifyContent='flex-start'
                                  width='100%'
                                  px='10px'
                                >
                                  <Typography
                                    noWrap
                                    textOverflow='ellipsis'
                                    variant='h5'
                                    color={item.children.at(0)?.diagram_link ? 'white' : '#878889'}
                                    sx={{fontSize: 13}}
                                  >
                                    {item.children.at(0)?.name}
                                  </Typography>
                                  {item.children
                                    .at(0)
                                    ?.roles.some((r) =>
                                      selectedRoles?.includes(
                                        r.role.role_category.label
                                          ? r.role.role_category.label
                                          : r.role.role_category.name,
                                      ),
                                    ) &&
                                    item.children.at(0)?.diagram_link && (
                                      <Box minWidth='18px' height='100%' display='flex' alignItems='center'>
                                        <UserFilled width='18px' height='18px' style={{color: 'white'}} />
                                      </Box>
                                    )}
                                  {item.children.at(0)?.update && (
                                    <WarningAltFilled width='18px' height='18px' style={{color: 'white'}} />
                                  )}
                                </Stack>
                              </ProcessToolTip>
                            </ProcessBox>
                          </>
                        ) : (
                          <>
                            <ProcessBox
                              sx={{
                                bgcolor: childrenHaveLinks(item.children) ? '#6A3B3B' : '#E1E3E4',
                                border: childrenHaveLinks(item.children) ? 'inherit' : 'none',
                                '&:hover': {
                                  background: childrenHaveLinks(item.children)
                                    ? theme.palette.primary.main
                                    : theme.palette.grey[200],
                                  cursor: 'pointer',
                                },
                              }}
                              onClick={() => dispatch(setSelectedParentOpen(item.parent.id))}
                            >
                              <ProcessToolTip
                                title={
                                  <Typography variant='h5' color={theme.palette.grey[700]}>
                                    {item.parent.name}
                                  </Typography>
                                }
                                sx={{display: getToolTip(item.parent) ? 'inherit' : 'none'}}
                                placement='bottom-start'
                              >
                                <Stack
                                  direction='row'
                                  spacing='10px'
                                  alignItems='center'
                                  justifyContent='flex-start'
                                  width='100%'
                                  px='10px'
                                >
                                  <Typography
                                    noWrap
                                    textOverflow='ellipsis'
                                    variant='h5'
                                    color={childrenHaveLinks(item.children) ? 'white' : '#878889'}
                                    sx={{fontSize: 13}}
                                  >
                                    {item.parent.name}
                                  </Typography>
                                  {childrenHaveRoles(item.children) && childrenHaveLinks(item.children) && (
                                    <Box minWidth='18px' height='100%' display='flex' alignItems='center'>
                                      <UserFilled width='18px' height='18px' style={{color: 'white'}} />
                                    </Box>
                                  )}

                                  {item.parent.update && (
                                    <WarningAltFilled width='18px' height='18px' style={{color: 'white'}} />
                                  )}

                                  <Box flexGrow={1} display='flex' justifyContent='flex-end'>
                                    {parentOpen?.includes(item.parent.id) ? (
                                      <ChevronUp
                                        style={{color: childrenHaveLinks(item.children) ? 'white' : '#878889'}}
                                        width='18px'
                                        height='18px'
                                      />
                                    ) : (
                                      <ChevronDown
                                        style={{color: childrenHaveLinks(item.children) ? 'white' : '#878889'}}
                                        width='18px'
                                        height='18px'
                                      />
                                    )}
                                  </Box>
                                </Stack>
                              </ProcessToolTip>
                            </ProcessBox>
                          </>
                        )}
                      </Grid>
                    </>
                  </Grid>
                  <Collapse in={parentOpen?.includes(item.parent.id)}>
                    <Grid container direction='row' columns={32} mt={0.4} columnSpacing='5px'>
                      {item.children.map((child) => (
                        <React.Fragment key={`${child.name}.process.${child.id}`}>
                          <Grid xsOffset={child.ui_mark_offset} xs={child.ui_mark_duration} mb={0.4}>
                            <ProcessBox
                              sx={{
                                bgcolor: child.diagram_link ? 'white' : '#E1E3E4',
                                '&:hover': child.diagram_link && {
                                  background: '#F2F2F2',
                                  cursor: 'pointer',
                                },
                                border: child.diagram_link ? `1px solid ${'#7D5454'}` : 'none',
                                cursor: `url(${Block}), auto`,
                                color: child.diagram_link ? theme.palette.grey[700] : '#878889',
                              }}
                              key={child.name}
                              onClick={() => handleProcessClick(child)}
                            >
                              <ProcessToolTip
                                title={
                                  <Typography variant='h5' color={theme.palette.grey[700]}>
                                    {child.name}
                                  </Typography>
                                }
                                sx={{display: getToolTip(child) ? 'inherit' : 'none'}}
                                placement='bottom'
                              >
                                <Stack
                                  direction='row'
                                  spacing='10px'
                                  alignItems='center'
                                  justifyContent='flex-start'
                                  width='100%'
                                  px='10px'
                                >
                                  <Typography noWrap textOverflow='ellipsis' variant='h5' sx={{fontSize: 13}}>
                                    {child.name}
                                  </Typography>
                                  {child.roles.some((r) =>
                                    selectedRoles?.includes(
                                      r.role.role_category.label
                                        ? r.role.role_category.label
                                        : r.role.role_category.name,
                                    ),
                                  ) &&
                                    child?.diagram_link && (
                                      <Box minWidth='20px'>
                                        <UserFilled width='18px' height='18px' style={{color: 'inherit'}} />
                                      </Box>
                                    )}

                                  {child.update && (
                                    <WarningAltFilled width='18px' height='18px' style={{color: 'white'}} />
                                  )}
                                </Stack>
                              </ProcessToolTip>
                            </ProcessBox>
                          </Grid>
                          <Grid xs={32 - child.ui_mark_offset - child.ui_mark_duration} />
                        </React.Fragment>
                      ))}
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>
            ))}
          {!disablePhases.includes(selectedGroup) && (
            <Box
              position='absolute'
              top={0}
              left={0}
              width='100%'
              height='100%'
              zIndex={100}
              sx={{pointerEvents: 'none'}}
            >
              <Grid
                container
                alignContent='flex-start'
                direction='row'
                justifyContent='flex-start'
                mb={1}
                columns={32.5}
                height='100%'
                columnSpacing='5px'
              >
                {phases?.map((item) => (
                  <Grid xs={item.mark_width} md={item.mark_width} key={`Highlight-${item.name}`} height='100%'>
                    <Box
                      width='100%'
                      height='100%'
                      bgcolor={item.id === currPhase?.id ? highlightColor : 'inherit'}
                      sx={{opacity: 0.1}}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
export default ProcessNavigation;
